/**
 * Installed vendor plugins, and the two writes that change them.
 *
 * `detect` is cheap and read-only, so it runs on mount and after every write —
 * an install that reports success but leaves the manifest missing should show as
 * `attention` immediately, not after the user reloads.
 *
 * Failures are values, not exceptions. The provisioner's `revit-running` is a
 * sentence with a fix in it, and the pane renders it beside the plugin it
 * concerns; throwing would strand it in a console.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DetectSnapshot,
  PluginInstallReport,
  PluginPatchReport,
  PluginVendor,
  ProvisionOutcome,
} from "../bimgo";
import { reloadRunningPlugins } from "../lib/pluginReload";
import type { PluginReloadResult } from "../lib/pluginReload";
import { openHostYears, pluginVendorsForBridge } from "../lib/plugins";

export type PluginsState =
  | { kind: "unsupported" }
  | { kind: "loading" }
  | { kind: "failed"; code: string; message: string }
  | { kind: "loaded"; snapshots: Partial<Record<PluginVendor, DetectSnapshot>> };

export interface PluginPatchResult extends PluginPatchReport {
  reload: PluginReloadResult | null;
}

export interface PluginsController {
  state: PluginsState;
  /** A fresh detect is pending; the previous loaded snapshot may still be visible. */
  refreshing: boolean;
  busy: boolean;
  canPatch: boolean;
  reload: () => void;
  install: (
    vendor: PluginVendor,
    versions: string[],
  ) => Promise<ProvisionOutcome<PluginInstallReport>>;
  /**
   * Apply a hot patch for these years, then ask matching running instances to reload.
   * Minor and major changes must use `install` because they may replace the pinned host.
   */
  patch: (vendor: PluginVendor, versions: string[]) => Promise<ProvisionOutcome<PluginPatchResult>>;
  /**
   * Which of these years have their CAD host open right now — a fresh detect, not the
   * snapshot on screen, which can be minutes old (#189). Asked before a whole-tree write so
   * the user is told to close the host first. A detect that fails answers "none open": the
   * provisioner still refuses an install under an open host, so this only ever adds a prompt.
   */
  openYears: (vendor: PluginVendor, versions: string[]) => Promise<string[]>;
  uninstall: (vendor: PluginVendor, versions: string[]) => Promise<ProvisionOutcome<unknown>>;
}

function unsupported<T>(): ProvisionOutcome<T> {
  return {
    ok: false,
    code: "unsupported",
    message: "this desktop build cannot manage plugins",
  };
}

/** Disabled until sign-in so detection can overlap the post-login loading phase. */
export function usePlugins(enabled = true): PluginsController {
  const bridge = window.bimgo?.plugins;
  const [state, setState] = useState<PluginsState>(
    bridge ? { kind: "loading" } : { kind: "unsupported" },
  );
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(enabled && Boolean(bridge));
  const [attempt, setAttempt] = useState(0);
  const [canPatch, setCanPatch] = useState(false);
  const mounted = useRef(true);
  const vendorAware = useRef(false);

  useEffect(() => {
    mounted.current = enabled;
    if (!enabled) {
      // A sign-out starts a fresh detection next time, rather than retaining the previous login.
      setState(bridge ? { kind: "loading" } : { kind: "unsupported" });
      setRefreshing(false);
      return () => { mounted.current = false; };
    }
    if (!bridge) return () => undefined;
    let cancelled = false;
    setRefreshing(true);

    // Only blank to "loading" on the first look. A re-detect (after an install/remove) keeps the
    // snapshot already on screen until the fresh one lands — otherwise the pane and the open
    // Manage dialog flash an empty state during the round-trip.
    setState((prev) => (prev.kind === "loaded" ? prev : { kind: "loading" }));
    void (async () => {
      const bridgeVersion = await window.bimgo?.app?.info()
        .then((info) => info.bridgeVersion)
        .catch(() => 0) ?? 0;
      vendorAware.current = bridgeVersion >= 7;
      setCanPatch(typeof bridge.patch === "function");
      const vendors = pluginVendorsForBridge(bridgeVersion);
      const outcomes = await Promise.all(
        vendors.map(async (vendor) => ({
          vendor,
          outcome: vendorAware.current ? await bridge.detect(vendor) : await bridge.detect(),
        })),
      );
      return outcomes;
    })()
      .then((outcomes) => {
        if (cancelled) return;
        const snapshots: Partial<Record<PluginVendor, DetectSnapshot>> = {};
        for (const { vendor, outcome } of outcomes) {
          if (outcome.ok) snapshots[vendor] = outcome.result;
        }
        if (Object.keys(snapshots).length > 0) {
          setState({ kind: "loaded", snapshots });
          return;
        }
        const failed = outcomes[0]?.outcome;
        setState(
          failed && !failed.ok
            ? { kind: "failed", code: failed.code, message: failed.message }
            : { kind: "failed", code: "detect", message: "plugin detection returned no result" },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // An IPC rejection is the bridge itself failing — distinct from the
        // provisioner answering with a failure, which is `failed` above.
        setState({
          kind: "failed",
          code: "bridge",
          message: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });

    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [bridge, attempt, enabled]);

  const reload = useCallback(() => {
    setRefreshing(Boolean(bridge));
    setAttempt((n) => n + 1);
  }, [bridge]);

  /**
   * Re-detect one vendor and swap its snapshot in, **before** the write that caused it is
   * reported done.
   *
   * The write used to return first and bump `attempt`, leaving the re-detect to the effect
   * above. Between those two moments the pane showed the *old* snapshot with the row's
   * button re-enabled — "Install" beside a plugin that had just been installed — until the
   * provisioner and the pointer answered, which is seconds on a slow asset host. Awaiting the
   * refresh here keeps the row busy until the new state is actually on screen, so the button
   * changes exactly once, to the right thing.
   *
   * A refresh that fails falls back to a full reload rather than leaving the stale snapshot
   * in place: a failed install can still have moved files, and a stale "not installed"
   * beside a half-written tree is the worst of the three answers.
   */
  const refreshVendor = useCallback(
    async (vendor: PluginVendor): Promise<void> => {
      if (!bridge) return;
      try {
        const outcome = vendorAware.current ? await bridge.detect(vendor) : await bridge.detect();
        if (!mounted.current) return;
        if (outcome.ok) {
          setState((prev) => ({
            kind: "loaded",
            snapshots: { ...(prev.kind === "loaded" ? prev.snapshots : {}), [vendor]: outcome.result },
          }));
          return;
        }
      } catch {
        // Fall through to the full reload below.
      }
      if (mounted.current) setAttempt((n) => n + 1);
    },
    [bridge],
  );

  const write = useCallback(
    async <T,>(
      action: "install" | "uninstall",
      vendor: PluginVendor,
      versions: string[],
    ): Promise<ProvisionOutcome<T>> => {
      if (!bridge) return unsupported<T>();
      setBusy(true);
      try {
        const outcome = (vendorAware.current
          ? await bridge[action](vendor, versions)
          : vendor === "revit"
            ? await bridge[action](versions)
            : unsupported<T>()) as ProvisionOutcome<T>;
        // Re-detect either way, and only report done once the new state is on screen.
        await refreshVendor(vendor);
        return outcome;
      } catch (error: unknown) {
        return {
          ok: false,
          code: "bridge",
          message: error instanceof Error ? error.message : String(error),
        };
      } finally {
        // The controller survives sign-out, so completed writes must release it even while disabled.
        setBusy(false);
      }
    },
    [bridge, refreshVendor],
  );

  const patch = useCallback(
    async (
      vendor: PluginVendor,
      versions: string[],
    ): Promise<ProvisionOutcome<PluginPatchResult>> => {
      if (!bridge?.patch) return unsupported<PluginPatchResult>();
      setBusy(true);
      try {
        if (!vendorAware.current && vendor !== "revit") {
          return unsupported<PluginPatchResult>();
        }
        const outcome = vendorAware.current
          ? await bridge.patch(vendor, versions)
          : await bridge.patch(versions);
        if (outcome.ok) {
          const reloadVersions = outcome.result.years.filter(
            (version) => !outcome.result.restartRequiredYears.includes(version),
          );
          const reload = reloadVersions.length > 0
            ? await reloadRunningPlugins(vendor, reloadVersions)
            : null;
          // The version label follows the patch only once the re-detect has landed.
          await refreshVendor(vendor);
          return { ok: true, result: { ...outcome.result, reload } };
        }
        // Re-detect so the version label follows the patch while the host remains open.
        await refreshVendor(vendor);
        return outcome;
      } catch (error: unknown) {
        return {
          ok: false,
          code: "bridge",
          message: error instanceof Error ? error.message : String(error),
        };
      } finally {
        setBusy(false);
      }
    },
    [bridge, refreshVendor],
  );

  const openYears = useCallback(
    async (vendor: PluginVendor, versions: string[]): Promise<string[]> => {
      // A bridge older than v7 detects Revit only, and cannot install anything else either.
      if (!bridge || (!vendorAware.current && vendor !== "revit")) return [];
      try {
        const outcome = vendorAware.current ? await bridge.detect(vendor) : await bridge.detect();
        if (!outcome.ok) return [];
        if (mounted.current) {
          setState((prev) => ({
            kind: "loaded",
            snapshots: { ...(prev.kind === "loaded" ? prev.snapshots : {}), [vendor]: outcome.result },
          }));
        }
        return openHostYears(outcome.result, versions);
      } catch {
        return [];
      }
    },
    [bridge],
  );

  return {
    state,
    refreshing,
    busy,
    canPatch,
    reload,
    openYears,
    install: (vendor, versions) => write<PluginInstallReport>("install", vendor, versions),
    patch,
    uninstall: (vendor, versions) => write<unknown>("uninstall", vendor, versions),
  };
}
